import React, { useEffect, useRef } from "react";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const mix = (a, b, t) => a + (b - a) * t;
const smooth = (t) => t * t * (3 - 2 * t);
const damp = (a, b, lambda, dt) => mix(a, b, 1 - Math.exp(-lambda * dt));

const FULL_TURN = Math.PI * 2;
const ABOUT_YAW = -0.42; // docked beside About, he faces the copy on his left

/* One fixed canvas that follows placeholders in the page:
   [data-avatar-slot="hero"] and, on desktop, [data-avatar-slot="about"].
   Scrolling glides the avatar from the first to the second with a full spin;
   the pointer steers his head and hovering him gets a smile. The page ends on
   a copy of the hero ([data-avatar-slot="loop"]) where he stands exactly as he
   does at the top, so the scroll can wrap around unnoticed. */
function AvatarStage() {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    const hero = document.querySelector('[data-avatar-slot="hero"]');
    const about = document.querySelector('[data-avatar-slot="about"]');
    const loop = document.querySelector('[data-avatar-slot="loop"]');
    if (!stage || !hero) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pointer = { x: 0, y: 0, tracking: false, tapUntil: 0 };
    const size = { width: 0, height: 0, stickyTop: 0 };
    let avatar = null;
    let disposed = false;
    let raf = 0;
    let last = performance.now();
    let travel = null; // 0 = in the hero slot … 1 = docked in the About slot
    let lean = 0;
    let lastScroll = window.scrollY;
    let hovering = false;

    const measure = () => {
      size.width = hero.offsetWidth;
      size.height = hero.offsetHeight;
      size.stickyTop = about ? parseFloat(getComputedStyle(about).top) || 0 : 0;
      stage.style.width = `${size.width}px`;
      stage.style.height = `${size.height}px`;
      if (avatar && size.width && size.height) avatar.resize(size.width, size.height);
    };

    const onMove = (e) => {
      if (e.pointerType === "touch") return;
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.tracking = true;
    };
    // Touch has no hover: a tap makes him look at your finger (and grin if it's on him)
    const onDown = (e) => {
      if (e.pointerType !== "touch") return;
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.tapUntil = performance.now() + 2200;
    };
    const onLeave = () => {
      pointer.tracking = false;
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      if (!size.width) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const b = about ? about.getBoundingClientRect() : null;
      const canDock = !!b && b.width > 0;
      // Once the hero copy at the end of the page scrolls in, he belongs to it
      const copy = loop ? loop.getBoundingClientRect() : null;
      const looping = !!copy && copy.width > 0 && copy.top < vh;
      const a = looping ? copy : hero.getBoundingClientRect();

      // Progress runs from the top of the page to where the About slot sticks, so
      // he holds his height on screen and drifts sideways while the page moves
      let goal = 0;
      if (canDock) {
        const dockScroll = about.parentElement.getBoundingClientRect().top + window.scrollY - size.stickyTop;
        goal = dockScroll > 0 ? clamp(window.scrollY / dockScroll, 0, 1) : 1;
      }
      travel = travel === null || reduce ? goal : damp(travel, goal, 7, dt);
      if (looping) travel = 0; // as at the top of the page, ready for the wrap
      const e = smooth(travel);

      const width = canDock ? mix(a.width, b.width, e) : a.width;
      const height = canDock ? mix(a.height, b.height, e) : a.height;
      const x = canDock ? mix(a.left, b.left, e) : a.left;
      const y = canDock ? mix(a.top, b.top, travel) : a.top;

      const visible = y + height > -40 && y < vh + 40;
      stage.style.visibility = visible ? "visible" : "hidden";
      if (!visible) return;
      stage.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${width / size.width})`;

      // Head and eyes follow the pointer; with no pointer he looks around on his own
      const cx = x + width / 2;
      const cy = y + height * 0.4;
      let lookX = 0;
      let lookY = 0;
      let hover = 0;
      if (pointer.tracking || now < pointer.tapUntil) {
        lookX = clamp((pointer.x - cx) / (vw * 0.5), -1, 1);
        lookY = clamp((pointer.y - cy) / (vh * 0.5), -1, 1);
        const dx = (pointer.x - cx) / (width * 0.42);
        const dy = (pointer.y - (y + height / 2)) / (height * 0.5);
        hover = dx * dx + dy * dy < 1 ? 1 : 0;
      } else if (!reduce) {
        lookX = Math.sin(now * 0.0005) * 0.35;
        lookY = Math.sin(now * 0.00037) * 0.15;
      }
      // The site cursor shrinks to a dot over him so it never hides his face
      if (hovering !== !!hover) {
        hovering = !!hover;
        document.body.classList.toggle("avatar-hover", hovering);
      }

      // Scroll: a full turn on the way to About (desktop) or a turn-away as the
      // hero leaves (mobile), plus a lean that follows scroll speed
      let spin = 0;
      if (reduce) {
        spin = canDock ? ABOUT_YAW * e : 0;
        lean = 0;
      } else {
        if (canDock) spin = (ABOUT_YAW - FULL_TURN) * e;
        else if (!looping) spin = -1.1 * clamp(window.scrollY / vh, 0, 1);
        // The wrap-around is a jump of the whole page length, not real speed
        const wrapped = Math.abs(window.scrollY - lastScroll) > vh * 2;
        const velocity = dt > 0 && !wrapped ? (window.scrollY - lastScroll) / dt : 0;
        lean = damp(lean, clamp(velocity * 0.00012, -0.16, 0.16), 5, dt);
      }
      lastScroll = window.scrollY;

      avatar.update({ dt, time: now / 1000, lookX, lookY, hover, spin, lean, still: reduce });
    };

    const fail = () => [hero, loop].forEach((slot) => slot && slot.classList.add("avatar-slot--failed"));

    // three.js is loaded on demand so it stays out of the main bundle; without
    // WebGL the build rejects and the slot shows the portrait photo instead
    import("./avatar/createAvatar")
      .then(({ createAvatar }) => (disposed ? null : createAvatar(canvasRef.current)))
      .then((built) => {
        if (!built) return;
        if (disposed) {
          built.dispose();
          return;
        }
        avatar = built;
        measure();
        stage.classList.add("avatar-stage--ready");
        last = performance.now();
        raf = requestAnimationFrame(frame);
      })
      .catch(fail);

    const observer = new ResizeObserver(measure);
    observer.observe(hero);
    window.addEventListener("resize", measure);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    measure();

    return () => {
      disposed = true;
      document.body.classList.remove("avatar-hover");
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      if (avatar) avatar.dispose();
    };
  }, []);

  return (
    <div ref={stageRef} className="avatar-stage" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}

export default AvatarStage;
